import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  approveAdminReport,
  applyGlobalAdminReportThresholds,
  generateAdminOilGeekDraft,
  getAdminReportReview,
  getAdminReportThresholdTemplateOptions,
  getThresholdsConfig,
  saveAdminReportReviewNote,
  saveAdminReportThresholds,
} from "../../services/api";

const s = {
  backdrop: { position: "fixed", inset: 0, zIndex: 1200, background: "rgba(15, 23, 42, 0.58)", display: "flex", alignItems: "center", justifyContent: "center", padding: 18 },
  modal: { width: "min(1080px, 96vw)", maxHeight: "90vh", overflow: "auto", background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 8, boxShadow: "0 24px 80px rgba(15, 23, 42, 0.35)", padding: 16 },
  row: { display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" },
  header: { display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", marginBottom: 14 },
  title: { fontSize: 18, fontWeight: 900, color: "var(--text-primary)" },
  hint: { fontSize: 12, color: "var(--text-muted)", lineHeight: 1.5 },
  label: { fontSize: 11, fontWeight: 800, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.08em" },
  subTitle: { fontSize: 14, fontWeight: 900, color: "var(--text-primary)", margin: "14px 0 8px" },
  panel: { border: "1px solid var(--border)", borderRadius: 8, padding: 14, background: "var(--bg-card)", marginTop: 12 },
  textarea: { width: "100%", minHeight: 110, resize: "vertical", border: "1.5px solid var(--border)", borderRadius: 6, background: "var(--dashboard-selector-bg)", color: "var(--text-primary)", padding: 10, font: "inherit", boxSizing: "border-box" },
  textareaWrap: { position: "relative", marginTop: 8 },
  draftingOverlay: { position: "absolute", inset: 0, minHeight: 110, border: "1.5px solid var(--teal-border)", borderRadius: 6, background: "color-mix(in srgb, var(--bg-card) 86%, transparent)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 2, pointerEvents: "auto", backdropFilter: "blur(1px)" },
  draftingBox: { width: "min(380px, 100%)", border: "1px solid var(--border)", borderRadius: 8, background: "var(--bg-card)", boxShadow: "0 16px 44px rgba(15, 23, 42, 0.16)", padding: 14 },
  draftingTitle: { fontSize: 14, fontWeight: 900, color: "var(--text-primary)", display: "flex", alignItems: "center", gap: 8 },
  draftingHint: { fontSize: 12, color: "var(--text-muted)", marginTop: 5 },
  draftingBar: { height: 6, borderRadius: 999, background: "var(--border-subtle)", overflow: "hidden", marginTop: 12 },
  draftingFill: { height: "100%", width: "42%", borderRadius: 999, background: "var(--teal)" },
  input: { border: "1.5px solid var(--border)", borderRadius: 6, background: "var(--dashboard-selector-bg)", color: "var(--text-primary)", padding: "7px 9px", width: "100%", boxSizing: "border-box" },
  btn: { border: "none", borderRadius: 6, padding: "8px 12px", background: "var(--teal)", color: "#fff", fontWeight: 800, cursor: "pointer" },
  secondary: { border: "1px solid var(--border)", borderRadius: 6, padding: "8px 12px", background: "var(--dashboard-selector-bg)", color: "var(--text-primary)", fontWeight: 800, cursor: "pointer" },
  danger: { border: "1px solid #fecaca", borderRadius: 6, padding: "8px 12px", background: "#fef2f2", color: "#b91c1c", fontWeight: 800, cursor: "pointer" },
  tabBar: { display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14, borderBottom: "1px solid var(--border)", paddingBottom: 10 },
  tab: { border: "1px solid var(--border)", borderRadius: 6, padding: "8px 11px", background: "var(--dashboard-selector-bg)", color: "var(--text-primary)", fontWeight: 900, cursor: "pointer" },
  tabActive: { border: "1px solid var(--teal)", borderRadius: 6, padding: "8px 11px", background: "var(--teal)", color: "#fff", fontWeight: 900, cursor: "pointer" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: { textAlign: "left", borderBottom: "1px solid var(--border)", padding: 8, color: "var(--text-muted)", fontSize: 11, textTransform: "uppercase" },
  thContent: { display: "inline-flex", alignItems: "center", gap: 6 },
  infoWrap: { position: "relative", display: "inline-flex", alignItems: "center" },
  infoButton: { width: 18, height: 18, borderRadius: 999, border: "1px solid color-mix(in srgb, var(--teal) 35%, transparent)", background: "color-mix(in srgb, var(--teal) 12%, transparent)", color: "var(--teal-dark)", fontSize: 12, fontWeight: 900, lineHeight: 1, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", padding: 0 },
  infoPopover: { position: "fixed", zIndex: 1400, width: 320, maxWidth: "min(320px, 78vw)", padding: 12, border: "1px solid var(--border)", borderRadius: 8, background: "var(--bg-card)", color: "var(--text-primary)", boxShadow: "0 18px 50px rgba(15, 23, 42, 0.22)", textTransform: "none", letterSpacing: 0, fontSize: 12, lineHeight: 1.45 },
  td: { borderBottom: "1px solid var(--border-subtle)", padding: 8, color: "var(--text-primary)", verticalAlign: "top" },
  statusOk: { fontSize: 13, fontWeight: 800, color: "#16a34a", marginTop: 10 },
  statusErr: { fontSize: 13, fontWeight: 800, color: "#dc2626", marginTop: 10 },
  badge: { display: "inline-flex", alignItems: "center", border: "1px solid var(--border)", borderRadius: 999, padding: "4px 8px", fontSize: 11, fontWeight: 900, color: "var(--text-muted)", background: "var(--dashboard-selector-bg)" },
};

function clone(value) {
  return JSON.parse(JSON.stringify(value || {}));
}

function ThresholdTypeInfo() {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const buttonRef = useRef(null);
  const show = () => {
    const rect = buttonRef.current?.getBoundingClientRect();
    setAnchor(rect ? {
      top: Math.max(16, Math.min(rect.bottom + 8, window.innerHeight - 220)),
      left: Math.max(16, Math.min(rect.left, window.innerWidth - 340)),
    } : null);
    setOpen(true);
  };
  return (
    <span style={s.infoWrap} onMouseEnter={show} onMouseLeave={() => setOpen(false)}>
      <button ref={buttonRef} type="button" style={s.infoButton} aria-label="Threshold type information" onClick={(event) => {
        event.stopPropagation();
        if (open) setOpen(false);
        else show();
      }}>i</button>
      {open && (
        <span style={{ ...s.infoPopover, ...(anchor || { top: 180, left: 24 }) }}>
          <strong>Threshold Type</strong>
          <br />
          <strong>Greater Than:</strong> standard higher-is-worse evaluation. Values at or above Warn show Watch; values at or above Fail show Critical.
          <br />
          <strong>BOOL:</strong> non-numeric flag values such as water/glycol. Numeric pass/warn/fail fields are not used.
          <br />
          <strong>VISCOSITY_100 / VISCOSITY_40:</strong> grade-band evaluation. Warn is the low boundary and Pass is the high boundary; too low is Critical, high is Watch, severely high is Critical.
        </span>
      )}
    </span>
  );
}

function ViscosityMapResolveInfo() {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const buttonRef = useRef(null);
  const show = () => {
    const rect = buttonRef.current?.getBoundingClientRect();
    setAnchor(rect ? {
      top: Math.max(16, Math.min(rect.bottom + 8, window.innerHeight - 220)),
      left: Math.max(16, Math.min(rect.left, window.innerWidth - 340)),
    } : null);
    setOpen(true);
  };
  return (
    <span style={s.infoWrap} onMouseEnter={show} onMouseLeave={() => setOpen(false)}>
      <button ref={buttonRef} type="button" style={s.infoButton} aria-label="Resolve from viscosity map information" onClick={(event) => {
        event.stopPropagation();
        if (open) setOpen(false);
        else show();
      }}>i</button>
      {open && (
        <span style={{ ...s.infoPopover, ...(anchor || { top: 180, left: 24 }) }}>
          <strong>Auto-Select from Viscosity Map</strong>
          <br />
          Chooses the best threshold template for this report from the sample details: component type, fuel type, and oil viscosity.
          <br />
          The viscosity map converts oil viscosity into a class such as 30, 40, 50, 90, or 140, then loads the matching engine, diesel, leaded/race, gear oil, or ATF/PSF template.
          <br />
          It only updates the threshold editor for the current report. It does not change global templates or affect customers until you save custom thresholds or approve/release the report.
        </span>
      )}
    </span>
  );
}

function TypeHeaderCell() {
  return (
    <th style={s.th}>
      <span style={s.thContent}>Type <ThresholdTypeInfo /></span>
    </th>
  );
}

function normalizeLegacyThresholdTypes(value) {
  if (Array.isArray(value)) return value.map(normalizeLegacyThresholdTypes);
  if (!value || typeof value !== "object") return value;
  return Object.fromEntries(Object.entries(value).map(([key, child]) => [
    key,
    key === "type" && child === "ICON" ? "GT" : normalizeLegacyThresholdTypes(child),
  ]));
}

function cloneThresholds(value) {
  return normalizeLegacyThresholdTypes(clone(value));
}

function reportIdFromDetail(detail, sampleId) {
  return sampleId || detail?.report?.lab_sample_id || detail?.report?.labSampleId || detail?.sample?.lab_sample_id || "";
}

function reportEmailFromDetail(detail, fallback = "") {
  return detail?.report?.customer_email || detail?.report?.customerEmail || detail?.sample?.customer_email || fallback || "";
}

function normalizeThresholds(thresholds) {
  return Object.fromEntries(Object.entries(normalizeLegacyThresholdTypes(thresholds) || {}).sort(([a], [b]) => a.localeCompare(b)));
}

function formatDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

const THRESHOLD_TYPE_OPTIONS = [
  ["GT", "Greater Than"],
  ["BOOL", "BOOL"],
  ["VISCOSITY_100", "VISCOSITY_100"],
  ["VISCOSITY_40", "VISCOSITY_40"],
];

function normalizeForMatch(value) {
  return String(value || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function sampleFromDetail(detail) {
  return detail?.report || detail?.sample || {};
}

function isInactiveThreshold(threshold) {
  return !threshold || (threshold.pass === 0 && threshold.warn === 0 && threshold.fail === 0);
}

function resolveTemplate(config, sample, viscosityMap) {
  const templates = config?.templates || {};
  const component = String(sample.component_type || sample.componentType || "").trim();
  const fuel = String(sample.vehicle_fuel_type || sample.vehicleFuelType || "").trim();
  const viscosity = String(sample.oil_viscosity || sample.oilViscosity || "").trim();
  const viscosityClass = viscosityMap?.[viscosity] || "30";

  if (component === "Transmission") return "ATF-PSF";
  if (component === "Gear Box") return viscosityClass === "140" ? "GEAROIL-140" : "GEAROIL-90";
  if (fuel === "Diesel") return templates[`DIESEL-${viscosityClass}`] ? `DIESEL-${viscosityClass}` : "DIESEL-40";
  if (fuel.includes("Leaded") || fuel.includes("Race Fuel")) return templates[`Leaded-${viscosityClass}`] ? `Leaded-${viscosityClass}` : "Leaded-50";
  return templates[`ENGINE-${viscosityClass}`] ? `ENGINE-${viscosityClass}` : "ENGINE-30";
}

function applyConditionalRules(template, rules, sample) {
  const next = clone(template);
  const apply = (metric, threshold, source) => {
    if (!next[metric] || isInactiveThreshold(next[metric]) || !threshold) return;
    next[metric] = { ...next[metric], ...threshold, _conditionalSource: source };
  };

  if (rules?.FUEL_DILUTION) {
    const fuel = String(sample.vehicle_fuel_type || sample.vehicleFuelType || "").toLowerCase();
    apply("FUEL_DILUTION", fuel.includes("diesel") ? rules.FUEL_DILUTION.diesel : rules.FUEL_DILUTION.gasoline, rules.FUEL_DILUTION.source);
  }

  if (rules?.TIN && sample.molybdenum !== null && sample.molybdenum !== undefined && sample.molybdenum !== "") {
    const molybdenum = Number(sample.molybdenum);
    if (!Number.isNaN(molybdenum)) {
      apply("TIN", molybdenum > 100 ? rules.TIN.molybdenum_gt_100 : rules.TIN.molybdenum_lte_100, rules.TIN.source);
    }
  }

  const oxidationRule = rules?.OXIDATION;
  if (oxidationRule) {
    const brand = normalizeForMatch(sample.oil_brand || sample.oilBrand);
    const brandRule = Object.entries(oxidationRule.brand_thresholds || {}).find(([brandName]) =>
      brand.includes(normalizeForMatch(brandName))
    )?.[1];
    const warn = brandRule?.warn ?? oxidationRule.default?.warn ?? oxidationRule.warn;
    const fail = brandRule?.fail ?? oxidationRule.default?.fail ?? oxidationRule.default_fail;
    apply("OXIDATION", { pass: 0, warn, fail, type: "GT" }, oxidationRule.source);
  }

  const titaniumRule = rules?.TITANIUM;
  if (titaniumRule) {
    const brand = normalizeForMatch(sample.oil_brand || sample.oilBrand);
    const brandRule = Object.entries(titaniumRule.brand_thresholds || {}).find(([brandName]) =>
      brand.includes(normalizeForMatch(brandName))
    )?.[1];
    if (brandRule) apply("TITANIUM", { ...brandRule, type: "GT" }, titaniumRule.source);
  }

  return next;
}

function setNestedValue(source, path, value) {
  const next = clone(source);
  let target = next;
  for (let index = 0; index < path.length - 1; index += 1) target = target[path[index]];
  target[path[path.length - 1]] = Number(value);
  return next;
}

function ThresholdFields({ label, value, path, updateConditional, fields = ["pass", "warn", "fail"] }) {
  if (!value) return null;
  return (
    <div style={{ ...s.row, alignItems: "flex-end", marginTop: 8 }}>
      <strong style={{ minWidth: 190, color: "var(--text-primary)", fontSize: 13 }}>{label}</strong>
      {fields.filter((field) => value[field] !== undefined).map((field) => (
        <label key={field} style={{ width: 110 }}>
          <span style={{ ...s.hint, display: "block", marginBottom: 3 }}>{field}</span>
          <input style={s.input} type="number" value={value[field]} onChange={(event) => updateConditional([...path, field], event.target.value)} />
        </label>
      ))}
    </div>
  );
}

function ConditionalRulesEditor({ rules, updateConditional }) {
  const oxidationBrands = Object.entries(rules?.OXIDATION?.brand_thresholds || {});
  const titaniumBrands = Object.entries(rules?.TITANIUM?.brand_thresholds || {});
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={s.subTitle}>Conditional Rules</div>

      {rules?.FUEL_DILUTION && (
        <div style={{ ...s.panel, marginTop: 8 }}>
          <div style={s.label}>Fuel Dilution</div>
          <ThresholdFields label="Gasoline" value={rules.FUEL_DILUTION.gasoline} path={["FUEL_DILUTION", "gasoline"]} updateConditional={updateConditional} />
          <ThresholdFields label="Diesel" value={rules.FUEL_DILUTION.diesel} path={["FUEL_DILUTION", "diesel"]} updateConditional={updateConditional} />
        </div>
      )}

      {rules?.TIN && (
        <div style={{ ...s.panel, marginTop: 8 }}>
          <div style={s.label}>Tin</div>
          <ThresholdFields label="Molybdenum <= 100" value={rules.TIN.molybdenum_lte_100} path={["TIN", "molybdenum_lte_100"]} updateConditional={updateConditional} />
          <ThresholdFields label="Molybdenum > 100" value={rules.TIN.molybdenum_gt_100} path={["TIN", "molybdenum_gt_100"]} updateConditional={updateConditional} />
        </div>
      )}

      {rules?.OXIDATION && (
        <div style={{ ...s.panel, marginTop: 8 }}>
          <div style={s.label}>Oxidation Oil Brand Overrides</div>
          <ThresholdFields label="Default" value={rules.OXIDATION.default} path={["OXIDATION", "default"]} updateConditional={updateConditional} fields={["warn", "fail"]} />
          {oxidationBrands.map(([brand, value]) => (
            <ThresholdFields key={brand} label={brand} value={value} path={["OXIDATION", "brand_thresholds", brand]} updateConditional={updateConditional} fields={["pass", "warn", "fail"]} />
          ))}
        </div>
      )}

      {rules?.TITANIUM && titaniumBrands.length > 0 && (
        <div style={{ ...s.panel, marginTop: 8 }}>
          <div style={s.label}>Titanium Oil Brand Overrides</div>
          {titaniumBrands.map(([brand, value]) => (
            <ThresholdFields key={brand} label={brand} value={value} path={["TITANIUM", "brand_thresholds", brand]} updateConditional={updateConditional} fields={["pass", "warn", "fail"]} />
          ))}
        </div>
      )}

      {Object.keys(rules || {}).length === 0 && <div style={s.hint}>No conditional rules loaded.</div>}
    </div>
  );
}

export default function ReportReviewModal({ sampleId, detail, customerEmail = "", embedded = false, dashboardReview = false, onClose, onReturn, onSaved, onApproved }) {
  const finalSampleId = reportIdFromDetail(detail, sampleId);
  const sample = sampleFromDetail(detail);
  const [note, setNote] = useState(detail?.oilGeekNote || detail?.review?.oilGeekNote || "");
  const [review, setReview] = useState(detail?.review || null);
  const [thresholdConfig, setThresholdConfig] = useState(null);
  const [thresholdSources, setThresholdSources] = useState([]);
  const [thresholdSourceId, setThresholdSourceId] = useState("");
  const [viscosityMap, setViscosityMap] = useState({});
  const [conditionalRules, setConditionalRules] = useState({});
  const [thresholds, setThresholds] = useState(() => cloneThresholds(detail?.review?.customThresholds || detail?.thresholds));
  const [templateName, setTemplateName] = useState(detail?.review?.templateName || detail?.templateName || "");
  const [thresholdTab, setThresholdTab] = useState("template");
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const autoDraftedRef = useRef("");

  useEffect(() => {
    let active = true;
    if (!finalSampleId) return () => {};
    autoDraftedRef.current = "";
    getAdminReportReview(finalSampleId)
      .then((res) => {
        if (!active) return;
        const nextReview = res.data.review;
        setReview(nextReview);
        setNote(nextReview?.oilGeekNote || "");
        if (nextReview?.thresholdMode === "custom" && nextReview.customThresholds) {
          setThresholds(cloneThresholds(nextReview.customThresholds));
          setTemplateName(nextReview.templateName || detail?.templateName || "");
        }
      })
      .catch(() => {
        if (active) setMessage({ type: "err", text: "Unable to load saved report review state." });
      });
    return () => { active = false; };
  }, [finalSampleId, detail?.templateName]);

  useEffect(() => {
    if (!finalSampleId || !review || review.reviewStatus === "approved") return;
    if (autoDraftedRef.current === finalSampleId) return;
    if (note.trim() || review?.oilGeekNote) return;
    autoDraftedRef.current = finalSampleId;
    generateAiDraft({ mode: "draft", overwrite: false, automatic: true });
  }, [finalSampleId, review, note]);

  useEffect(() => {
    let active = true;
    Promise.allSettled([getThresholdsConfig(), getAdminReportThresholdTemplateOptions()])
      .then(([configResult, sourceResult]) => {
        if (!active) return;
        const config = configResult.status === "fulfilled" ? configResult.value.data || {} : {};
        const normalizedConfig = normalizeLegacyThresholdTypes(config);
        const sources = sourceResult.status === "fulfilled"
          ? (sourceResult.value.data?.sources || []).map((source) => ({
            ...source,
            config: normalizeLegacyThresholdTypes(source.config),
          }))
          : [];
        const fallbackSource = { id: "published-current", label: "Published Global Thresholds", status: "published", updatedAt: null, config: normalizedConfig };
        const nextSources = sources.length > 0 ? sources : [fallbackSource];
        const selectedSource = nextSources.find((source) => source.status === "published") || nextSources[0];

        setThresholdSources(nextSources);
        setThresholdSourceId(selectedSource?.id || "");
        setThresholdConfig(selectedSource?.config || normalizedConfig);
        setViscosityMap(clone((selectedSource?.config || normalizedConfig).template_selection_rules?.viscosity_class_mapping));
        setConditionalRules(cloneThresholds(config.active_rules));
      })
      .catch(() => {
        if (active) setMessage({ type: "err", text: "Unable to load global threshold controls." });
      });
    return () => { active = false; };
  }, []);

  const rows = useMemo(() => Object.entries(normalizeThresholds(thresholds)), [thresholds]);
  const templateNames = useMemo(() => Object.keys(thresholdConfig?.templates || {}).sort(), [thresholdConfig]);
  const mode = review?.thresholdMode || detail?.thresholdMode || "global";
  const email = reportEmailFromDetail(detail, customerEmail);

  function selectThresholdSource(sourceId) {
    const source = thresholdSources.find((item) => item.id === sourceId);
    if (!source?.config) return;
    const nextConfig = source.config;
    const nextViscosityMap = clone(nextConfig.template_selection_rules?.viscosity_class_mapping);
    const resolved = resolveTemplate(nextConfig, sample, nextViscosityMap);
    const nextTemplateName = nextConfig.templates?.[templateName] ? templateName : resolved;
    const template = nextConfig.templates?.[nextTemplateName] || Object.values(nextConfig.templates || {})[0];
    const finalTemplateName = nextConfig.templates?.[nextTemplateName] ? nextTemplateName : Object.keys(nextConfig.templates || {})[0] || "";

    setThresholdSourceId(sourceId);
    setThresholdConfig(nextConfig);
    setViscosityMap(nextViscosityMap);
    setTemplateName(finalTemplateName);
    if (template) setThresholds(cloneThresholds(applyConditionalRules(template, conditionalRules, sample)));
    setMessage({ type: "ok", text: `${source.label} loaded for this report. Save custom thresholds to apply it.` });
  }

  function loadTemplate(nextTemplateName) {
    const template = thresholdConfig?.templates?.[nextTemplateName];
    if (!template) return;
    setTemplateName(nextTemplateName);
    setThresholds(cloneThresholds(applyConditionalRules(template, conditionalRules, sample)));
  }

  function resolveFromViscosityMap() {
    const resolved = resolveTemplate(thresholdConfig, sample, viscosityMap);
    loadTemplate(resolved);
    setMessage({ type: "ok", text: `Resolved template ${resolved} loaded for this report.` });
  }

  function applyRulesToCurrentTemplate() {
    const template = thresholdConfig?.templates?.[templateName];
    if (!template) {
      setMessage({ type: "err", text: "Select a valid template before applying rules." });
      return;
    }
    setThresholds(cloneThresholds(applyConditionalRules(template, conditionalRules, sample)));
    setMessage({ type: "ok", text: "Conditional rules applied to this report's custom thresholds." });
  }

  function updateThreshold(metric, field, value) {
    setThresholds((current) => {
      const next = clone(current);
      next[metric] = { ...(next[metric] || {}) };
      next[metric][field] = field === "type" ? value : Number(value);
      return next;
    });
  }

  function updateViscosity(viscosity, value) {
    setViscosityMap((current) => ({ ...current, [viscosity]: value }));
  }

  function updateConditional(path, value) {
    setConditionalRules((current) => setNestedValue(current, path, value));
  }

  async function saveNote() {
    setLoading(true);
    setMessage(null);
    try {
      const res = await saveAdminReportReviewNote(finalSampleId, { note });
      setReview(res.data.review);
      setMessage({ type: "ok", text: "Oil Geek note saved." });
      onSaved?.({ review: res.data.review });
    } catch (err) {
      setMessage({ type: "err", text: err.response?.data?.error || "Unable to save note." });
    } finally {
      setLoading(false);
    }
  }

  async function generateAiDraft({ mode = "draft", overwrite = false, automatic = false } = {}) {
    const isEnhance = mode === "enhance";
    if (isEnhance && !note.trim()) {
      setMessage({ type: "err", text: "Add or generate a note before enhancing it." });
      return;
    }
    if (overwrite && review?.oilGeekNoteSource === "admin" && note.trim() && !isEnhance) {
      const confirmed = window.confirm("Replace the current admin-edited note with a new AI draft?");
      if (!confirmed) return;
    }
    setDrafting(true);
    setMessage(automatic ? { type: "ok", text: "Drafting Oil Geek note..." } : null);
    try {
      const res = await generateAdminOilGeekDraft(finalSampleId, { overwrite: overwrite || isEnhance, mode, note: isEnhance ? note : undefined });
      if (res.data.status === "skipped" || res.data.status === "disabled") {
        if (!automatic) setMessage({ type: "ok", text: res.data.reason || "AI draft skipped." });
        else if (res.data.status === "disabled") setMessage({ type: "ok", text: res.data.reason || "Oil Geek AI drafting is disabled." });
        return;
      }
      if (res.data.status && res.data.status !== "drafted") {
        setMessage({ type: "err", text: res.data.reason || "AI draft did not complete." });
        return;
      }
      const nextReview = res.data.review || {};
      setReview((current) => ({ ...(current || {}), ...nextReview }));
      setNote(res.data.draft?.note || nextReview.oilGeekNote || "");
      setMessage({ type: "ok", text: isEnhance ? "Note enhanced. Review before approving." : "AI draft generated. Review and edit before approving." });
      onSaved?.(res.data);
    } catch (err) {
      if (automatic) autoDraftedRef.current = "";
      setMessage({ type: "err", text: err.response?.data?.error || (isEnhance ? "Unable to enhance note." : "Unable to generate AI draft.") });
    } finally {
      setDrafting(false);
    }
  }

  async function saveThresholds() {
    setLoading(true);
    setMessage(null);
    try {
      const normalizedThresholds = normalizeLegacyThresholdTypes(thresholds);
      const res = await saveAdminReportThresholds(finalSampleId, { templateName, thresholds: normalizedThresholds });
      setReview(res.data.review);
      setThresholds(cloneThresholds(res.data.thresholds || normalizedThresholds));
      setTemplateName(res.data.templateName || templateName);
      setMessage({ type: "ok", text: "Custom report thresholds saved." });
      onSaved?.(res.data);
    } catch (err) {
      setMessage({ type: "err", text: err.response?.data?.error || "Unable to save custom thresholds." });
    } finally {
      setLoading(false);
    }
  }

  async function applyGlobal() {
    setLoading(true);
    setMessage(null);
    try {
      const res = await applyGlobalAdminReportThresholds(finalSampleId);
      setReview(res.data.review);
      setThresholds(cloneThresholds(res.data.thresholds || {}));
      setTemplateName(res.data.templateName || "");
      setMessage({ type: "ok", text: "Global thresholds applied to this report." });
      onSaved?.(res.data);
    } catch (err) {
      setMessage({ type: "err", text: err.response?.data?.error || "Unable to apply global thresholds." });
    } finally {
      setLoading(false);
    }
  }

  async function approveReport(disposition = "release") {
    setLoading(true);
    setMessage(null);
    try {
      const res = await approveAdminReport(finalSampleId, disposition, note);
      setReview(res.data.review);
      setMessage({ type: "ok", text: disposition === "save" ? "Note saved and report approved for the release queue." : "Note saved and report released to the customer dashboard." });
      onSaved?.(res.data);
      onApproved?.(res.data);
    } catch (err) {
      setMessage({ type: "err", text: err.response?.data?.error || "Unable to approve report." });
    } finally {
      setLoading(false);
    }
  }

  const content = (
    <>
        {dashboardReview && (
          <div className="sdx-admin-review-dock">
            <div style={{ ...s.panel,margin:0,borderColor:"var(--teal)",boxShadow:"0 10px 30px rgba(15,23,42,.14)" }}>
              <div style={{ ...s.row, justifyContent:"space-between" }}>
                <div><div style={s.label}>Report Review</div><div style={s.hint}>{finalSampleId} · Review the dashboard results, edit the customer note, then approve.</div></div>
                <div style={s.row}>
                  <button type="button" style={s.secondary} onClick={onReturn} disabled={loading||drafting}>Back to Report List</button>
                  <button type="button" style={s.secondary} onClick={saveNote} disabled={loading||drafting}>Save Draft</button>
                  <button type="button" style={s.secondary} onClick={()=>approveReport("save")} disabled={loading||drafting}>Approve and Save</button>
                  <button type="button" style={s.btn} onClick={()=>approveReport("release")} disabled={loading||drafting}>Approve and Release</button>
                </div>
              </div>
            </div>
          </div>
        )}
        {!embedded && (
          <div style={s.header}>
          <div>
            <div style={s.title}>Thresholds and Notes</div>
            <div style={s.hint}>{finalSampleId}{email ? ` - ${email}` : ""}</div>
          </div>
          <button type="button" style={s.secondary} onClick={onClose}>Close</button>
          </div>
        )}

        {!embedded && (
          <div style={{ ...s.panel, borderColor: review?.reviewStatus === "approved" ? "#16a34a" : "var(--border)" }}>
            <div style={{ ...s.row, justifyContent: "space-between" }}>
              <div>
                <div style={s.label}>Customer Release</div>
                <div style={s.hint}>
                  {review?.reviewStatus === "approved"
                    ? `Approved${review.approvedBy ? ` by ${review.approvedBy}` : ""}${review.approvedAt ? ` on ${formatDate(review.approvedAt)}` : ""}.`
                    : "Queued or eligible reports stay pending for customers until approved."}
                </div>
              </div>
              <button type="button" style={s.btn} onClick={approveReport} disabled={loading || review?.reviewStatus === "approved"}>
                {review?.reviewStatus === "approved" ? "Approved" : "Approve and Release"}
              </button>
            </div>
          </div>
        )}

        <div style={s.panel}>
          <div style={{ ...s.row, justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <div style={s.label}>Note from the Oil Geek</div>
              <div style={{ ...s.row, gap: 6, marginTop: 6 }}>
                {review?.oilGeekNoteSource && <span style={s.badge}>{review.oilGeekNoteSource === "ai" ? "AI draft" : "Admin edited"}</span>}
                {review?.oilGeekAiStatus && <span style={s.badge}>AI: {review.oilGeekAiStatus}</span>}
                {review?.oilGeekAiModel && <span style={s.badge}>{review.oilGeekAiModel}</span>}
              </div>
            </div>
            <div style={s.row}>
              <button type="button" style={s.secondary} onClick={() => generateAiDraft({ mode: "enhance", overwrite: true })} disabled={loading || drafting || !Boolean(note.trim())}>
                Polish this note
              </button>
              <button type="button" style={s.secondary} onClick={() => generateAiDraft({ mode: "draft", overwrite: true })} disabled={loading || drafting}>
                Regenerate Draft
              </button>
            </div>
          </div>
          <div style={s.textareaWrap}>
            <textarea
              style={{ ...s.textarea, opacity: drafting ? 0.48 : 1 }}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              readOnly={drafting}
              aria-busy={drafting ? "true" : "false"}
            />
            {drafting && (
              <div style={s.draftingOverlay} role="status" aria-live="polite">
                <div style={s.draftingBox}>
                  <div style={s.draftingTitle}>
                    <span className="sdx-drafting-spinner" aria-hidden="true" />
                    Drafting Oil Geek note<span className="sdx-drafting-dots" aria-hidden="true" />
                  </div>
                  <div style={s.draftingHint}>Review text is being generated. Please wait before editing or approving.</div>
                  <div style={s.draftingBar}>
                    <div className="sdx-drafting-bar" style={s.draftingFill} />
                  </div>
                </div>
              </div>
            )}
          </div>
          <div style={{ ...s.row, justifyContent: "space-between", marginTop: 10 }}>
            <div style={s.hint}>
              This note is visible to the customer on the dashboard and PDF report. AI drafts must be reviewed before approval.
              {review?.oilGeekAiError ? ` Last AI error: ${review.oilGeekAiError}` : ""}
            </div>
            <button type="button" style={s.btn} onClick={saveNote} disabled={loading}>Save Note</button>
          </div>
        </div>

        {dashboardReview && <button type="button" style={{...s.secondary,marginTop:12}} onClick={()=>setShowAdvanced((value)=>!value)}>{showAdvanced?"Hide Advanced Thresholds":"Advanced Thresholds"}</button>}
        {(!dashboardReview||showAdvanced) && <div style={s.panel}>
          <div style={{ ...s.row, justifyContent: "space-between", marginBottom: 10 }}>
            <div>
              <div style={s.label}>Thresholds</div>
              <div style={s.hint}>Current mode: {mode === "custom" ? "Custom values saved for this report" : "Global values"}</div>
            </div>
            <div style={s.row}>
              <button type="button" style={s.secondary} onClick={applyGlobal} disabled={loading}>Use Global Thresholds</button>
              <button type="button" style={s.btn} onClick={saveThresholds} disabled={loading || rows.length === 0}>Save Custom Thresholds</button>
            </div>
          </div>

          <div style={s.tabBar}>
            {[
              ["template", "Threshold Template"],
              ["viscosity", "Viscosity Map"],
              ["conditions", "Conditional Rules"],
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                style={thresholdTab === key ? s.tabActive : s.tab}
                onClick={() => setThresholdTab(key)}
              >
                {label}
              </button>
            ))}
          </div>

          {thresholdTab === "template" && (
            <>
              <div style={{ ...s.row, alignItems: "flex-end", marginBottom: 12 }}>
                <label style={{ display: "block", minWidth: 300 }}>
                  <span style={{ ...s.label, display: "block", marginBottom: 6 }}>Saved Threshold Template</span>
                  <select style={s.input} value={thresholdSourceId} onChange={(event) => selectThresholdSource(event.target.value)}>
                    {thresholdSources.map((source) => (
                      <option key={source.id} value={source.id}>
                        {source.label}{source.status === "draft" ? " (Draft)" : ""}{source.updatedAt ? ` - ${formatDate(source.updatedAt)}` : ""}
                      </option>
                    ))}
                  </select>
                </label>
                <label style={{ display: "block", minWidth: 260 }}>
                  <span style={{ ...s.label, display: "block", marginBottom: 6 }}>Applied Template</span>
                  <select style={s.input} value={templateName} onChange={(event) => loadTemplate(event.target.value)}>
                    {!templateNames.includes(templateName) && templateName && <option value={templateName}>{templateName}</option>}
                    {templateNames.map((name) => <option key={name} value={name}>{name}</option>)}
                  </select>
                </label>
                <span style={{ ...s.row, gap: 6, flexWrap: "nowrap" }}>
                  <button type="button" style={s.secondary} onClick={resolveFromViscosityMap} disabled={!thresholdConfig}>Auto-Select from Viscosity Map</button>
                  <ViscosityMapResolveInfo />
                </span>
                <button type="button" style={s.secondary} onClick={applyRulesToCurrentTemplate} disabled={!thresholdConfig}>Apply Conditional Rules</button>
              </div>

              <div style={s.subTitle}>Resolved Custom Thresholds</div>
              <div style={{ overflowX: "auto", maxHeight: 480, border: "1px solid var(--border-subtle)", borderRadius: 8 }}>
                <table style={s.table}>
                  <thead>
                    <tr><th style={s.th}>Metric</th><th style={s.th}>Pass</th><th style={s.th}>Warn</th><th style={s.th}>Fail</th><TypeHeaderCell /></tr>
                  </thead>
                  <tbody>
                    {rows.map(([metric, threshold]) => {
                      const isParticleCount = metric === "PARTICLE_COUNT";
                      const boolDisplay = threshold.type === "BOOL" && threshold.pass === undefined && threshold.warn === undefined && threshold.fail === undefined;
                      return (
                        <tr key={metric}>
                          <td style={s.td}>{metric}</td>
                          {isParticleCount ? (
                            <td style={s.td} colSpan="4">
                              <div style={{display:"flex",gap:18,alignItems:"center",flexWrap:"wrap"}}>
                                <span><strong>Good through:</strong> {(threshold.goodMax||[23,20,17]).join(" / ")}</span>
                                <span><strong>Fail starting:</strong> {(threshold.failMin||[26,23,20]).join(" / ")}</span>
                                <span style={s.hint}>Watch between · Worst component controls · Global across all templates</span>
                              </div>
                            </td>
                          ) : ["pass", "warn", "fail"].map((field) => (
                            <td style={s.td} key={field}>
                              {boolDisplay ? <span style={s.hint}>N/A</span> : (
                                <input style={s.input} type="number" value={threshold[field] ?? ""} onChange={(event) => updateThreshold(metric, field, event.target.value)} />
                              )}
                            </td>
                          ))}
                          {!isParticleCount && <td style={s.td}>
                            <select style={s.input} value={threshold.type === "ICON" ? "GT" : threshold.type || "GT"} onChange={(event) => updateThreshold(metric, "type", event.target.value)}>
                              {THRESHOLD_TYPE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                            </select>
                          </td>}
                        </tr>
                      );
                    })}
                    {rows.length === 0 && <tr><td style={s.td} colSpan="5">Open a ready report with resolved thresholds before saving custom values.</td></tr>}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {thresholdTab === "viscosity" && (
            <>
              <div style={{ ...s.row, justifyContent: "space-between", alignItems: "flex-end", marginBottom: 10 }}>
                <div>
                  <div style={s.subTitle}>Viscosity Map</div>
                  <div style={s.hint}>Edit map values, then return to Threshold Template and resolve from the map.</div>
                </div>
                <span style={{ ...s.row, gap: 6, flexWrap: "nowrap" }}>
                  <button type="button" style={s.secondary} onClick={() => { resolveFromViscosityMap(); setThresholdTab("template"); }} disabled={!thresholdConfig}>Auto-select Template from here</button>
                  <ViscosityMapResolveInfo />
                </span>
              </div>
              <div style={{ overflowX: "auto", maxHeight: 480, border: "1px solid var(--border-subtle)", borderRadius: 8 }}>
                <table style={s.table}>
                  <thead><tr><th style={s.th}>Oil Viscosity</th><th style={s.th}>Template Class</th></tr></thead>
                  <tbody>
                    {Object.entries(viscosityMap || {}).map(([viscosity, mappedClass]) => (
                      <tr key={viscosity}>
                        <td style={s.td}>{viscosity}</td>
                        <td style={s.td}><input style={{ ...s.input, maxWidth: 140 }} value={mappedClass} onChange={(event) => updateViscosity(viscosity, event.target.value)} /></td>
                      </tr>
                    ))}
                    {Object.keys(viscosityMap || {}).length === 0 && <tr><td style={s.td} colSpan="2">No viscosity map loaded.</td></tr>}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {thresholdTab === "conditions" && (
            <>
              <div style={{ ...s.row, justifyContent: "space-between", alignItems: "flex-end", marginBottom: 10 }}>
                <div>
                  <div style={s.subTitle}>Conditional Rules</div>
                  <div style={s.hint}>Edit rules, then apply them to the currently selected template.</div>
                </div>
                <button type="button" style={s.secondary} onClick={() => { applyRulesToCurrentTemplate(); setThresholdTab("template"); }} disabled={!thresholdConfig}>Apply Rules</button>
              </div>
              <ConditionalRulesEditor rules={conditionalRules} updateConditional={updateConditional} />
            </>
          )}
        </div>}

        {message && <div style={message.type === "ok" ? s.statusOk : s.statusErr}>{message.text}</div>}
    </>
  );

  if (embedded) return content;

  return (
    <div style={s.backdrop} onMouseDown={onClose}>
      <section style={s.modal} onMouseDown={(event) => event.stopPropagation()}>
        {content}
      </section>
    </div>
  );
}
